import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChartMovement, ChartRank, LoadingState, MonoLabel, PageHeader, Segmented, Star } from '@/component';
import { useBillboard, useFavourites, type BillboardScope } from '@/hook';
import { formatQueueCount } from '@/lib/chart';
import { cn } from '@/lib/cn';

const ROWS = 40;
const SCOPES = [
  { value: 'global', label: 'Global' },
  { value: 'friends', label: 'Friends' },
] as const satisfies ReadonlyArray<{ value: BillboardScope; label: string }>;

const GRID = 'grid grid-cols-[40px_minmax(0,2.4fr)_minmax(0,1.6fr)_100px_70px_26px] items-center gap-[18px]';

/** "Week of Sep 7" — the Monday the running chart counts from. */
export function weekEyebrow(weekStartMs: number): string {
  const day = new Date(weekStartMs).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  return `Week of ${day} · queued in sessions`;
}

/** /billboard: the full chart. Rank 1 breaks the grid — bigger and gold — and
 * is the only place in the app that does. */
export function BillboardPage() {
  const [scope, setScope] = useState<BillboardScope>('global');
  const chart = useBillboard(scope, ROWS);
  const { favourites, toggle } = useFavourites();

  return (
    <>
      <PageHeader
        eyebrow={chart?.weekStartMs ? weekEyebrow(chart.weekStartMs) : 'Queued in sessions'}
        title="The billboard"
        action={<Segmented options={SCOPES} value={scope} onChange={setScope} />}
      />
      <div className={cn(GRID, 'mt-10 items-baseline border-b border-line pb-3')}>
        <MonoLabel>#</MonoLabel>
        <MonoLabel>Song</MonoLabel>
        <MonoLabel>Artist</MonoLabel>
        <MonoLabel className="text-right">Queued</MonoLabel>
        <MonoLabel className="text-right">Move</MonoLabel>
        <span />
      </div>
      {chart === undefined && <LoadingState label="Loading the chart…" />}
      {chart && chart.rows.length === 0 && (
        <p className="py-10 text-sm text-mute">
          Nothing queued yet this week. The chart fills as songs go on Aeon systems.
        </p>
      )}
      <div key={scope}>
        {chart?.rows.map((row, index) => (
          <div
            key={row.trackId}
            className={cn(GRID, 'chart-row-in border-b border-stripeA py-[17px]')}
            style={{ animationDelay: `${index * 25}ms` }}
          >
            <ChartRank rank={row.rank} className={row.rank === 1 ? 'text-[22px]' : 'text-[15px]'} />
            <Link
              to="/tracks/$trackId"
              params={{ trackId: row.trackId }}
              className={cn(
                'min-w-0 truncate font-semibold tracking-[-0.015em]',
                row.rank === 1 ? 'text-[20px]' : 'text-[15px]'
              )}
            >
              {row.title}
            </Link>
            <div className="truncate text-sm text-mute">{row.artist}</div>
            <div className="text-right font-mono text-[13px] tabular-nums">{formatQueueCount(row.count)}</div>
            <ChartMovement movement={row.movement} className="text-right" />
            <Star on={favourites.has(row.trackId)} onToggle={() => toggle(row.trackId)} className="w-auto" />
          </div>
        ))}
      </div>
    </>
  );
}
